import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Divider, Popover, FormGroup } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import PrimaryButton from "../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../Buttons/SecondaryButton/SecondaryButton";
import LoadBoardColumnsItem from "./LoadBoardColumnsItem/LoadBoardColumnsItem";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";

export default function LoadBoardColumns({
  tabValue,
  columns,
  setColumns,
  defaultColumns
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const pid = open ? "load-board-columns-popover" : undefined;
  const sensors = useSensors(useSensor(PointerSensor));

  const [customColumns, setCustomColumns] = useState([]);

  const handleReset = () => {
    setCustomColumns(defaultColumns);
    setColumns(defaultColumns);
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearch = () => {
    const updatedCustomColumns = [...customColumns];
    updatedCustomColumns.forEach((col, index) => {
      col.displayOrder = index + 1;
    });

    setColumns(customColumns);
    handleClose();
  };

  const handleDragEnd = event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setCustomColumns(cusCols => {
        const oldIndex = cusCols.findIndex(({ columnName }) => columnName === active.id);
        const newIndex = cusCols.findIndex(({ columnName }) => columnName === over.id);

        return arrayMove(cusCols, oldIndex, newIndex);
      });
    }
  };

  const handleActive = (name, value) => {
    setCustomColumns(old =>
      old.map(row => {
        if (row.title === name) {
          return {
            ...row,
            isChecked: value,
          };
        }
        return row;
      })
    );
  };

  useEffect(() => {
    if (columns.length > 0) {
      setCustomColumns(columns);
    } else {
      handleReset();
    }
  }, [tabValue]);

  return (
    <>
      <SecondaryButton
        label="Customize Columns"
        onClick={e => setAnchorEl(e.currentTarget)}
        startIcon={TuneIcon}
        size="small"
      />
      <Popover
        id={pid}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        slotProps={{
          paper: {
            sx: { boxShadow: "elevation3" },
          }
        }}
      >
        <Box sx={{ width: "280px", maxHeight: "70vh", padding: "20px", overflow: "auto" }}>
          <Box display="flex">
            <Typography variant="h4" color="black_opacity.100">Columns</Typography>
            <CloseIcon
              onClick={handleClose}
              sx={{ fontSize: "20px", color: "black_opacity.60", cursor: "pointer", marginLeft: "auto" }} />
          </Box>
          <Divider sx={{ color: "black_opacity.30", margin: "20px -20px" }} />
          <Box>
            <FormGroup>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
              >
                <SortableContext
                  items={customColumns.map(({ columnName }) => columnName)}
                  strategy={verticalListSortingStrategy}
                >
                  {customColumns.map(item => (
                    <LoadBoardColumnsItem key={item.displayOrder} item={item} handleActive={handleActive} />
                  ))}
                </SortableContext>
              </DndContext>
            </FormGroup>
          </Box>
          <Divider sx={{ color: "black_opacity.30", margin: "20px -20px" }} />
          <Box>
            <PrimaryButton
              label="Apply Columns"
              onClick={handleSearch}
              width="100%"
            />
          </Box>
          <Box sx={{ marginTop: "10px" }}>
            <SecondaryButton
              label="Reset Columns"
              onClick={handleReset}
              width="100%"
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
}

LoadBoardColumns.propTypes = {
  tabValue: PropTypes.number,
  columns: PropTypes.array.isRequired,
  setColumns: PropTypes.func.isRequired,
  defaultColumns: PropTypes.array
};